// Auto-payment: the sequence of rune taps that makes a card affordable.
//
// 164.2. A basic rune taps two ways, and they are not interchangeable:
//
//   [E]          exhaust it        -> 1 Energy, and the rune stays on the board
//   Recycle this send it to the    -> 1 Power of its own domain, and the rune
//                Rune Deck            is gone for the rest of the game
//
// Both are "abilities that add resources", so a payment search that takes the
// first one it finds will happily recycle runes to pay a cost that only needed
// them exhausted. That is a permanent loss the player never asked for, which is
// why the choice here is deliberate rather than greedy.

import { advance, applyAction, legalActions } from '../rules/engine.ts';
import { abilitiesOf, tapIsDestructive, totalCost } from '../rules/state.ts';
import type { Rules } from '../rules/state.ts';
import { DOMAINS } from '../rules/types.ts';
import type { Cost, Domain, RunePool } from '../rules/types.ts';
import type { Action, GameState, ObjectId } from '../rules/types.ts';

type Activate = Extract<Action, { t: 'activate' }>;


/** How much of `cost` a pool still cannot cover. */
function shortfall(pool: RunePool, cost: Cost): number {
  const domains = cost.powerDomains.length > 0 ? cost.powerDomains : DOMAINS;
  let power = pool.any;
  for (const d of domains) power += pool.power[d];
  return Math.max(0, cost.energy - pool.energy) + Math.max(0, cost.power - power);
}

/** The pool as it would stand after a tap, without touching the real one. */
function poolAfter(pool: RunePool, gives: TapYield): RunePool {
  const next: RunePool = {
    energy: pool.energy + gives.energy,
    power: { ...pool.power },
    any: pool.any,
    restricted: pool.restricted,
  };
  if (gives.power > 0) {
    if (gives.domain && gives.domain !== 'any') next.power[gives.domain] += gives.power;
    else next.any += gives.power;
  }
  return next;
}

/** What an activation puts in the pool, or null if it is not a resource tap. */
export interface TapYield {
  energy: number;
  power: number;
  /** The domain the Power arrives in; 'self' resolves to the rune's own. */
  domain: Domain | 'any' | null;
}

export function tapGives(
  state: GameState, rules: Rules, action: Activate,
): TapYield | null {
  const source = state.objects[action.object];
  if (!source) return null;
  const own = rules.index.get(source.card).domains[0] ?? null;
  for (const ability of abilitiesOf(rules, source.card)) {
    if (ability.kind !== 'activated' || ability.key !== action.ability) continue;
    let energy = 0;
    let power = 0;
    let domain: Domain | 'any' | null = null;
    for (const e of ability.effects) {
      if (e.op !== 'addResource') return null;
      energy += e.energy ?? 0;
      power += e.power ?? 0;
      // 164.2.b.1. [C] is Power of the rune's own domain.
      if (e.power) domain = e.domain === 'self' ? own : (e.domain ?? 'any');
    }
    return { energy, power, domain };
  }
  return null;
}

/** Whether paying this way spends the card itself rather than just tapping it. */
// Re-exported so the existing callers keep one import, and imported for
// this file's own use.
export { tapIsDestructive };

/**
 * Taps that would make `object` playable, or null if it cannot be afforded.
 *
 * Runs on a clone, so nothing here touches the real game. Only resource-adding
 * activations are considered: anything else is a decision the player should be
 * making themselves.
 */
/**
 * What the payment is *for*.
 *
 * Playing a card and activating an ability are priced differently -- one by the
 * card's cost, the other by the ability's -- and they finish on different
 * actions. Everything between is identical, and was worth generalising rather
 * than copying: without it, disabling manual rune taps would leave every paid
 * ability, Equip included, unusable.
 */
export type PaymentGoal =
  | { kind: 'play'; object: ObjectId }
  | { kind: 'activate'; object: ObjectId; ability: string };

/** The cost an ability asks for, in the shape the shortfall arithmetic wants. */
function abilityCost(rules: Rules, state: GameState, goal: PaymentGoal): Cost | null {
  if (goal.kind !== 'activate') return null;
  const source = state.objects[goal.object];
  if (!source) return null;
  const ability = abilitiesOf(rules, source.card).find(
    (a) => a.kind === 'activated' && a.key === goal.ability);
  if (!ability || ability.kind !== 'activated') return null;
  const domain = ability.cost.powerDomain;
  return {
    energy: ability.cost.energy ?? 0,
    power: ability.cost.power ?? 0,
    // 'self' is the source's own domain and 'any' is unrestricted; neither
    // narrows what may pay here, and guessing wrong would reject a legal tap.
    powerDomains: domain && domain !== 'any' && domain !== 'self' ? [domain] : [],
  };
}

export function findPaymentPath(
  state: GameState, rules: Rules, target: ObjectId | PaymentGoal,
): Action[] | null {
  const goal: PaymentGoal = typeof target === 'object' ? target : { kind: 'play', object: target };
  const object = goal.object;

  const copy = structuredClone(state);
  copy.events = [];

  const card = copy.objects[object];
  if (!card) return null;
  const def = rules.index.get(card.card);

  const path: Action[] = [];
  for (let step = 0; step < 24; step++) {
    const actions = legalActions(copy, rules);
    const done = goal.kind === 'play'
      ? actions.some((a) => a.t === 'play' && a.object === object)
      : actions.some((a) => a.t === 'activate' && a.object === object
        && a.ability === goal.ability);
    if (done) return path;

    const taps = actions.filter((a): a is Activate =>
      a.t === 'activate' && tapGives(copy, rules, a) !== null);

    // Least destructive first, so an exhaust is always tried before a recycle.
    const ranked = [...taps].sort((a, b) =>
      Number(tapIsDestructive(copy, rules, a)) - Number(tapIsDestructive(copy, rules, b)));

    // Each tap has to actually close the gap. Ranking alone is not enough: once
    // every rune is exhausted, the only taps left are recycles, and taking one
    // to pay an Energy cost it cannot pay spends the rune for nothing. The
    // projection is arithmetic on the pool rather than a simulated turn, which
    // keeps this cheap enough to run for every card in hand each frame.
    const pool = copy.players[card.controller].pool;
    const cost = abilityCost(rules, copy, goal)
      ?? totalCost(copy, rules, def, card.controller);
    const before = shortfall(pool, cost);

    let next: Activate | null = null;
    for (const tap of ranked) {
      const gives = tapGives(copy, rules, tap);
      if (!gives) continue;
      if (shortfall(poolAfter(pool, gives), cost) < before) { next = tap; break; }
    }
    if (!next) return null;

    if (!applyAction(copy, rules, next)) return null;
    advance(copy, rules);
    path.push(next);
  }
  return null;
}

/**
 * The plays a card would offer once `path` has been paid, without paying it.
 *
 * Pressing a card used to run the payment there and then, because pressing was
 * playing. Now that a play is only made when the card is dropped somewhere that
 * will take it, the destinations have to be known *before* the runes are spent
 * -- a drag that is called off must cost nothing, and a tap that buys nothing
 * is a rune thrown away when the pool empties (167.1).
 *
 * Runs on a clone, like the search that produced the path.
 */
export function playsAfterPaying(
  state: GameState, rules: Rules, object: ObjectId, path: readonly Action[],
): Action[] {
  const copy = structuredClone(state);
  copy.events = [];
  for (const step of path) {
    if (!applyAction(copy, rules, step)) return [];
    advance(copy, rules);
  }
  return legalActions(copy, rules).filter((a) => a.t === 'play' && a.object === object);
}
